import {useEffect, useRef, useState} from 'react';
import firebase from 'firebase/compat/app';
import {useAppSelector} from '../hooks';
import fb from '../firebase';
import {post} from '../../utils/frRequest';
import {
  TransferenciaCompletaPendente, TransferenciaContagemCompleta, TransferenciaExecucaoStatus,
  TransferenciaHistorico, TransferenciaItemTransferido, TransferenciaResumo, UsuarioParaSelecao,
} from '../../models/Transferencia';
import {DetalhesParciaisPorTipo, TransferenciaParcial} from '../../models/TransferenciaParcial';

export const useUserTipo = () => {
  const user = useAppSelector(state => state.app.user);
  const tipo = (user.tipo as string) || 'usuario';
  const regional = user.regional as string | undefined;
  const isAdmin = tipo === 'admin';
  const isGestor = tipo === 'gestor';
  return {tipo, regional, isAdmin, isGestor, podeAbrirTransferencia: isAdmin || isGestor};
};

const PAGE_SIZE = 20;

interface HistoricoTransferenciasResult {
  historico: TransferenciaHistorico[];
  isLoading: boolean;
  isLoadingMais: boolean;
  temMais: boolean;
  carregarMais: () => void;
}

export const useHistoricoTransferencias = (): HistoricoTransferenciasResult => {
  const {isAdmin} = useUserTipo();
  const user = useAppSelector(state => state.app.user);
  const [paginaInicial, setPaginaInicial] = useState<TransferenciaHistorico[]>([]);
  const [paginasExtras, setPaginasExtras] = useState<TransferenciaHistorico[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMais, setIsLoadingMais] = useState(false);
  const [temMais, setTemMais] = useState(false);
  const cursorRef = useRef<firebase.firestore.QueryDocumentSnapshot | null>(null);

  useEffect(() => {
    if (!user.uid) return;

    setIsLoading(true);
    setPaginasExtras([]);
    setTemMais(false);
    cursorRef.current = null;

    if (isAdmin) {
      return fb.db.collection('historico_transferencias')
        .orderBy('criado_em', 'desc')
        .limit(PAGE_SIZE)
        .onSnapshot(snap => {
          setIsLoading(false);
          setPaginaInicial(snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaHistorico)));
          cursorRef.current = snap.docs[snap.docs.length - 1] ?? null;
          setTemMais(snap.docs.length === PAGE_SIZE);
        });
    }

    // usuário não-admin: quatro queries separadas (de_uid, para_uid, aberto_por_uid, criado_por_uid
    // — as duas últimas cobrem transferências abertas por um gestor), resultado merged, sem paginação
    let deResults: TransferenciaHistorico[] = [];
    let paraResults: TransferenciaHistorico[] = [];
    let abertoPorResults: TransferenciaHistorico[] = [];
    let criadoPorResults: TransferenciaHistorico[] = [];
    let loadedCount = 0;
    const totalQueries = 4;

    const merge = () => {
      const seen = new Set<string>();
      const merged: TransferenciaHistorico[] = [];
      for (const item of [...deResults, ...paraResults, ...abertoPorResults, ...criadoPorResults]) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          merged.push(item);
        }
      }
      merged.sort((a, b) => {
        const aMs = a.criado_em?.toMillis?.() ?? 0;
        const bMs = b.criado_em?.toMillis?.() ?? 0;
        return bMs - aMs;
      });
      setPaginaInicial(merged);
    };

    const cancelDe = fb.db.collection('historico_transferencias')
      .where('de_uid', '==', user.uid)
      .onSnapshot(snap => {
        deResults = snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaHistorico));
        loadedCount++;
        if (loadedCount >= totalQueries) setIsLoading(false);
        merge();
      });

    const cancelPara = fb.db.collection('historico_transferencias')
      .where('para_uid', '==', user.uid)
      .onSnapshot(snap => {
        paraResults = snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaHistorico));
        loadedCount++;
        if (loadedCount >= totalQueries) setIsLoading(false);
        merge();
      });

    const cancelAbertoPor = fb.db.collection('historico_transferencias')
      .where('aberto_por_uid', '==', user.uid)
      .onSnapshot(snap => {
        abertoPorResults = snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaHistorico));
        loadedCount++;
        if (loadedCount >= totalQueries) setIsLoading(false);
        merge();
      });

    const cancelCriadoPor = fb.db.collection('historico_transferencias')
      .where('criado_por_uid', '==', user.uid)
      .onSnapshot(snap => {
        criadoPorResults = snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaHistorico));
        loadedCount++;
        if (loadedCount >= totalQueries) setIsLoading(false);
        merge();
      });

    return () => {
      cancelDe();
      cancelPara();
      cancelAbertoPor();
      cancelCriadoPor();
    };
  }, [isAdmin, user.uid]);

  const carregarMais = () => {
    if (!isAdmin || !cursorRef.current || isLoadingMais) return;

    setIsLoadingMais(true);
    fb.db.collection('historico_transferencias')
      .orderBy('criado_em', 'desc')
      .startAfter(cursorRef.current)
      .limit(PAGE_SIZE)
      .get()
      .then(snap => {
        const novosItens = snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaHistorico));
        setPaginasExtras(atual => [...atual, ...novosItens]);
        cursorRef.current = snap.docs[snap.docs.length - 1] ?? cursorRef.current;
        setTemMais(snap.docs.length === PAGE_SIZE);
      })
      .finally(() => setIsLoadingMais(false));
  };

  const idsPaginaInicial = new Set(paginaInicial.map(item => item.id));
  const historico = [
    ...paginaInicial,
    ...paginasExtras.filter(item => !idsPaginaInicial.has(item.id)),
  ];

  return {
    historico,
    isLoading,
    isLoadingMais,
    temMais: isAdmin && temMais,
    carregarMais,
  };
};

interface SolicitacoesParciaisResult {
  solicitacoes: TransferenciaParcial[];
  isLoading: boolean;
}

export const useSolicitacoesParciais = (): SolicitacoesParciaisResult => {
  const {isAdmin, isGestor} = useUserTipo();
  const user = useAppSelector(state => state.app.user);
  const [solicitacoes, setSolicitacoes] = useState<TransferenciaParcial[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin && !isGestor) {
      setIsLoading(false);
      setSolicitacoes([]);
      return;
    }

    setIsLoading(true);

    let query = fb.db.collection('transferencias_parciais')
      .where('status', 'in', ['aguardando_selecao', 'aguardando_aprovacao', 'aguardando_aprovacao_admin']) as
      firebase.firestore.Query;

    if (isGestor) {
      query = query.where('criado_por_uid', '==', user.uid);
    }

    return query.onSnapshot(snap => {
      setIsLoading(false);
      const itens = snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaParcial));
      itens.sort((a, b) => {
        const aMs = a.criado_em?.toMillis?.() ?? 0;
        const bMs = b.criado_em?.toMillis?.() ?? 0;
        return bMs - aMs;
      });
      setSolicitacoes(itens);
    });
  }, [isAdmin, isGestor, user.uid]);

  return {solicitacoes, isLoading};
};

interface UsuariosParaSelecaoResult {
  usuarios: UsuarioParaSelecao[];
  isLoading: boolean;
  erro: string;
}

export const useUsuariosParaSelecao = (regional?: string): UsuariosParaSelecaoResult => {
  const [usuarios, setUsuarios] = useState<UsuarioParaSelecao[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let ativo = true;
    setIsLoading(true);
    setErro('');

    post('/veApi/transferencias/listar_usuarios', regional ? {regional} : {})
      .then(res => {
        if (!ativo) return;
        setIsLoading(false);
        if (!res.success) {
          setErro(res.error || 'Ocorreu um erro ao carregar os usuários.');
          return;
        }
        setUsuarios((res.data as {usuarios: UsuarioParaSelecao[]}).usuarios);
      });

    return () => {
      ativo = false;
    };
  }, [regional]);

  return {usuarios, isLoading, erro};
};

interface TransferenciasCompletasPendentesResult {
  pendencias: TransferenciaCompletaPendente[];
  isLoading: boolean;
}

export const useTransferenciasCompletasPendentes = (): TransferenciasCompletasPendentesResult => {
  const {isAdmin} = useUserTipo();
  const [pendencias, setPendencias] = useState<TransferenciaCompletaPendente[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      setIsLoading(false);
      setPendencias([]);
      return;
    }

    setIsLoading(true);
    return fb.db.collection('transferencias_completas_pendentes')
      .where('status', 'in', ['aguardando_aprovacao_admin', 'em_andamento', 'erro'])
      .onSnapshot(snap => {
        setIsLoading(false);
        const itens = snap.docs.map(doc => ({id: doc.id, ...doc.data()} as TransferenciaCompletaPendente));
        itens.sort((a, b) => {
          const aMs = a.criado_em?.toMillis?.() ?? 0;
          const bMs = b.criado_em?.toMillis?.() ?? 0;
          return bMs - aMs;
        });
        setPendencias(itens);
      });
  }, [isAdmin]);

  return {pendencias, isLoading};
};

interface DetalhesParciaisResult {
  detalhes: DetalhesParciaisPorTipo | null;
  isLoading: boolean;
  erro: string;
  carregar: (id: string) => void;
}

export const useDetalhesParcial = (): DetalhesParciaisResult => {
  const [detalhes, setDetalhes] = useState<DetalhesParciaisPorTipo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = (id: string) => {
    setIsLoading(true);
    setErro('');

    post('/veApi/transferencias/listar_detalhes_parcial', {id})
      .then(res => {
        setIsLoading(false);
        if (!res.success) {
          setErro(res.error || 'Ocorreu um erro ao carregar os detalhes.');
          return;
        }
        setDetalhes(res.data as DetalhesParciaisPorTipo);
      });
  };

  return {detalhes, isLoading, erro, carregar};
};

interface ItensCompletaResult {
  itens: TransferenciaItemTransferido[] | null;
  isLoading: boolean;
  erro: string;
  carregar: (id: string) => void;
}

export const useItensCompleta = (): ItensCompletaResult => {
  const [itens, setItens] = useState<TransferenciaItemTransferido[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = (id: string) => {
    setIsLoading(true);
    setErro('');

    post('/veApi/transferencias/listar_itens_completa', {id})
      .then(res => {
        setIsLoading(false);
        if (!res.success) {
          setErro(res.error || 'Ocorreu um erro ao carregar os itens.');
          return;
        }
        setItens((res.data as {itens_transferidos: TransferenciaItemTransferido[]}).itens_transferidos);
      });
  };

  return {itens, isLoading, erro, carregar};
};

interface ContagemItensCompletaResult {
  contagem: TransferenciaContagemCompleta | null;
  isLoading: boolean;
  erro: string;
  carregar: (deUid: string) => void;
}

/**
 * Contagem de itens (fazendas/lojas/veterinários/proprietários) que uma transferência completa
 * moveria do de_uid informado — usada tanto no preview antes da confirmação (Passo 2 de
 * RealizarTransferenciaPage) quanto na aprovação de uma pendência, já que a contagem pode mudar
 * entre a abertura e a aprovação.
 */
export const useContagemItensCompleta = (): ContagemItensCompletaResult => {
  const [contagem, setContagem] = useState<TransferenciaContagemCompleta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = (deUid: string) => {
    setIsLoading(true);
    setErro('');
    setContagem(null);

    post('/veApi/transferencias/contar_itens_completa', {de_uid: deUid})
      .then(res => {
        setIsLoading(false);
        if (!res.success) {
          setErro(res.error || 'Ocorreu um erro ao carregar a contagem de itens.');
          return;
        }
        setContagem(res.data as TransferenciaContagemCompleta);
      });
  };

  return {contagem, isLoading, erro, carregar};
};

interface PendenciaCompletaAbertaResult {
  pendencia: TransferenciaCompletaPendente | null;
  isLoading: boolean;
}

export const usePendenciaCompletaAberta = (): PendenciaCompletaAbertaResult => {
  const {isGestor} = useUserTipo();
  const user = useAppSelector(state => state.app.user);
  const [pendencia, setPendencia] = useState<TransferenciaCompletaPendente | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isGestor || !user.uid) {
      setIsLoading(false);
      setPendencia(null);
      return;
    }

    setIsLoading(true);
    return fb.db.collection('transferencias_completas_pendentes')
      .where('aberto_por_uid', '==', user.uid)
      .where('status', '==', 'aguardando_aprovacao_admin')
      .limit(1)
      .onSnapshot(snap => {
        setIsLoading(false);
        const doc = snap.docs[0];
        setPendencia(doc ? ({id: doc.id, ...doc.data()} as TransferenciaCompletaPendente) : null);
      });
  }, [isGestor, user.uid]);

  return {pendencia, isLoading};
};

interface ContagemAprovacoesPendentesResult {
  completas: number;
  parciais: number;
}

/**
 * Quantidade de transferências aguardando decisão do usuário atual, separada por completa/parcial —
 * completa e parcial vivem em abas/menus separados (ver TransferenciasPage), então sem essa contagem
 * visível de fora o admin pode não perceber que há pendências no outro menu.
 */
export const useContagemAprovacoesPendentes = (): ContagemAprovacoesPendentesResult => {
  const {isAdmin} = useUserTipo();
  const user = useAppSelector(state => state.app.user);
  const {pendencias} = useTransferenciasCompletasPendentes();
  const {solicitacoes} = useSolicitacoesParciais();

  // mesmo critério de "pode aprovar" já usado por item em TransferenciaParcialPage/SolicitacaoCard
  const parciais = solicitacoes.filter(s => (
    (s.status === 'aguardando_aprovacao' && (
      (s.criado_por_tipo === 'admin' && isAdmin)
      || (s.criado_por_tipo === 'gestor' && user.uid === s.criado_por_uid)
    ))
    || (s.status === 'aguardando_aprovacao_admin' && isAdmin)
  )).length;

  // a listagem de pendências agora também inclui transferências diretas do admin em
  // 'em_andamento'/'erro' (ver completa-admin-visivel-pendentes) — não são "decisões pendentes",
  // então não contam pro indicador
  const completas = pendencias.filter(p => p.status === 'aguardando_aprovacao_admin').length;

  return {completas, parciais};
};

const POLL_EXECUCAO_MS = 10_000;
const AVISO_DEMORA_EXECUCAO_MS = 5 * 60 * 1000;

interface ExecucaoResult {
  status: 'em_andamento' | 'concluida' | 'erro' | null;
  resumo: TransferenciaResumo | null;
  erro: string;
  demorando: boolean;
}

/**
 * Consulta `endpoint` a cada 10s enquanto `execucaoId` estiver definido, parando assim que o
 * status virar concluida/erro — usado tanto pela transferência completa quanto pela parcial (as
 * duas rodam em background, fora da request que as disparou — ver
 * transferencia-completa-background/transferencia-parcial-background). `demorando` fica true
 * depois de ~5min sem concluir, um aviso brando pro usuário, não um estado de erro (o polling
 * continua).
 */
const usePollingExecucao = (endpoint: string, execucaoId: string | null): ExecucaoResult => {
  const [status, setStatus] = useState<'em_andamento' | 'concluida' | 'erro' | null>(null);
  const [resumo, setResumo] = useState<TransferenciaResumo | null>(null);
  const [erro, setErro] = useState('');
  const [demorando, setDemorando] = useState(false);

  useEffect(() => {
    if (!execucaoId) {
      setStatus(null);
      setResumo(null);
      setErro('');
      setDemorando(false);
      return;
    }

    let ativo = true;
    let intervalo: ReturnType<typeof setInterval> | null = null;
    const inicio = Date.now();

    setStatus('em_andamento');
    setResumo(null);
    setErro('');
    setDemorando(false);

    const parar = () => {
      if (intervalo !== null) {
        clearInterval(intervalo);
        intervalo = null;
      }
    };

    const consultar = () => {
      post(endpoint, {id: execucaoId})
        .then(res => {
          if (!ativo) return;

          if (!res.success) {
            setStatus('erro');
            setErro(res.error || 'Ocorreu um erro ao consultar o status da transferência.');
            parar();
            return;
          }

          const data = res.data as TransferenciaExecucaoStatus;
          setStatus(data.status);
          if (data.status === 'concluida') {
            setResumo(data.resumo || null);
            parar();
          } else if (data.status === 'erro') {
            setErro(data.erro || 'Ocorreu um erro ao processar a transferência.');
            parar();
          } else if (Date.now() - inicio > AVISO_DEMORA_EXECUCAO_MS) {
            setDemorando(true);
          }
        });
    };

    consultar();
    intervalo = setInterval(consultar, POLL_EXECUCAO_MS);

    return () => {
      ativo = false;
      parar();
    };
  }, [endpoint, execucaoId]);

  return {status, resumo, erro, demorando};
};

export const useExecucaoCompleta = (execucaoId: string | null): ExecucaoResult =>
  usePollingExecucao('/veApi/transferencias/consultar_execucao_completa', execucaoId);

export const useExecucaoParcial = (execucaoId: string | null): ExecucaoResult =>
  usePollingExecucao('/veApi/transferencias/consultar_execucao_parcial', execucaoId);

/**
 * Busca, uma vez, se já existe uma execução `em_andamento` vinculada a um valor de campo numa
 * coleção de execuções — usado pra recuperar o estado "processando" quando um card remonta (ex.:
 * usuário trocou de tela e voltou depois de já ter aprovado), já que qual execução está rodando é
 * estado local que não sobrevive ao unmount, mas o registro em si continua no Firestore.
 */
const useExecucaoEmAndamentoPor = (colecao: string, campo: string, valor: string): string | null => {
  const [execucaoId, setExecucaoId] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    fb.db.collection(colecao)
      .where(campo, '==', valor)
      .get()
      .then(snap => {
        if (!ativo || snap.empty) return;
        const maisRecente = snap.docs.reduce((atual, doc) => (
          (doc.data().criado_em?.toMillis?.() ?? 0) > (atual.data().criado_em?.toMillis?.() ?? 0) ? doc : atual
        ));
        if (maisRecente.data().status === 'em_andamento') {
          setExecucaoId(maisRecente.id);
        }
      });
    return () => {
      ativo = false;
    };
  }, [colecao, campo, valor]);

  return execucaoId;
};

export const useExecucaoEmAndamentoPorPendencia = (pendenciaId: string): string | null =>
  useExecucaoEmAndamentoPor('transferencias_completas_execucoes', 'pendencia_id', pendenciaId);

export const useExecucaoEmAndamentoPorSolicitacao = (solicitacaoId: string): string | null =>
  useExecucaoEmAndamentoPor('transferencias_parciais_execucoes', 'solicitacao_id', solicitacaoId);
